/**
 * In order to slide to items/groups from outside, you can trigger events
 * on the ".slider" wrap:
 * 	- slide-to-item (passing { itemIndex: number })
 * 	- slide-to-prev-item
 * 	- slide-to-next-item
 * 	- slide-to-group (passing { groupIndex: number })
 * 	- slide-to-prev-group
 * 	- slide-to-next-group
 *
 * You can listen for the ".slider" wrap's "change" event, which will contain
 * the following data:
 * 	- itemIndex
 * 	- maxIndex
 * 	- numGroups
 * 	- prevAllowed (bool)
 * 	- nextAllowed (bool)
 * 	
 */

import { debounce, throttle, ceil, floor, each, min, max, range } from "lodash";
import { getItemsPerGroupDataFromWrapAttr, getItemsPerGroup } from "./slider-helpers";
import * as Hammer from "hammerjs";

declare const jQuery: any;
const $ = jQuery;
const $window = $(window);

const config = Object.freeze({
	dfItemsPerGroup: [
		{ minWindowWidth: 0, nItems: 1 },
	]
});

export function initFiniteEqualWidthSliders(): void
{
	$(".finite-equal-width-slider").each(function () {
		initSlider($(this));
	});
}

export function initSlider($wrap): void
{
	const $display = $wrap.find(".slider-items-display");
	const $slider = $wrap.find(".slider-items-slider");
	const autoSlideWait: number|undefined = getAutoSlideWait($wrap);
	const itemsPerGroupSettings = getItemsPerGroupDataFromWrapAttr($wrap, config.dfItemsPerGroup);
	initSliderLayout($slider, $window, itemsPerGroupSettings);
	
	let itemIndex = 0;

	function getMaxIndex(): number
	{
		return calcMaxIndex($slider, $window, itemsPerGroupSettings);
	}
	
	function goTo(newIndex: number): void
	{
		const maxIndex: number = getMaxIndex();
		newIndex = min([max([0, newIndex]), maxIndex]);
		slideToItem($slider, $display, newIndex);
		const numGroups: number = ceil($slider.children().length / getItemsPerGroup($window, itemsPerGroupSettings));
		highlightGroupControl($wrap, getGroupIndex(newIndex, maxIndex, numGroups, $window, itemsPerGroupSettings));
		setPrevNextDisabled($wrap, newIndex, maxIndex);
		itemIndex = newIndex;
		$slider.trigger("change", {
			itemIndex,
			maxIndex,
			numGroups,
			prevAllowed: itemIndex > 0,
			nextAllowed: itemIndex < maxIndex
		});
	}

	function goToGroup(groupIndex: number): void
	{
		goTo(groupIndex * getItemsPerGroup($window, itemsPerGroupSettings));
	}

	const prevItem = () => goTo(itemIndex - 1);
	const nextItem = () => goTo(itemIndex + 1);
	const prevGroup = () => goTo(itemIndex - getItemsPerGroup($window, itemsPerGroupSettings));
	const nextGroup = () => goTo(itemIndex + getItemsPerGroup($window, itemsPerGroupSettings));
	
	initPrevAndNextGroupButtons($wrap, prevGroup, nextGroup);
	initNavControls($wrap, $slider, $window, goToGroup, itemsPerGroupSettings);
	initSwipe($wrap, prevGroup, nextGroup);
	highlightGroupControl($wrap, 0);
	setPrevNextDisabled($wrap, 0, getMaxIndex());

	$window.on("resize", throttle(() => {
		goTo(itemIndex);
	}, 50));

	$wrap.addClass("show");

	$wrap.on("slide-to-item", (e, data) => {
		if (data.itemIndex === undefined)
		{
			console.error("No itemIndex passed to slider slide-to-item handler");
			return;
		}
		goTo(data.itemIndex);
	});

	$wrap.on("slide-to-prev-item", (e, data) => {
		prevItem();
	});

	$wrap.on("slide-to-next-item", (e, data) => {
		nextItem();
	});

	$wrap.on("slide-to-group", (e, data) => {
		if (data.groupIndex === undefined)
		{
			console.error("No groupIndex passed to slider slide-to-group handler");
			return;
		}
		goToGroup(data.groupIndex);
	});

	$wrap.on("slide-to-prev-group", (e, data) => {
		prevGroup();
	});

	$wrap.on("slide-to-next-group", (e, data) => {
		nextGroup();
	});

	if (autoSlideWait)
	{
		const nextAutoSlide = () => {
			setTimeout(() => {
				if (itemIndex < getMaxIndex())
				{
					nextItem();
				}
				else
				{
					goTo(0);
				}
				nextAutoSlide();
			}, autoSlideWait);
		};
		nextAutoSlide();
	}
}

// Returns int milliseconds between autoslide, or undefined if no autoslide
function getAutoSlideWait($wrap): number|undefined
{
	const str: string = $wrap.attr("data-auto-slide-wait"); // secs
	if (!str)
	{
		return undefined;
	}
	const secs: number = parseFloat(str);
	return (secs && secs > 0) ? max([secs * 1000, 1000]) : undefined;
}

function initSwipe($wrap, prevGroup: Function, nextGroup: Function): void
{
	const hammer = new Hammer($wrap[0], {});
	hammer.on("swiperight", prevGroup);
	hammer.on("swipeleft", nextGroup);
}

function getGroupIndex(itemIndex: number, maxItemIndex: number, numGroups: number, $window, itemsPerGroupSettings): number
{
	if (itemIndex >= maxItemIndex)
	{
		return numGroups - 1;
	}
	const groupIndex: number = floor(itemIndex / getItemsPerGroup($window, itemsPerGroupSettings));
	if (groupIndex === 0 && itemIndex > 0 && numGroups > 1)
	{
		return 1;
	}
	return groupIndex;
}

function initSliderLayout($slider, $window, itemsPerGroupSettings): void
{
	const fixLayout = () => setWidths($slider, $window, itemsPerGroupSettings);
	fixLayout();
	$window.on("load resize", throttle(fixLayout, 50));
}

function initPrevAndNextGroupButtons($wrap, prevGroup: () => void, nextGroup: () => void): void
{
	$wrap.find(`[data-slider-ctrl-prev]`).click(function (ev) {
		prevGroup();
		return false;
	});
	$wrap.find(`[data-slider-ctrl-next]`).click(function (ev) {
		nextGroup();
		return false;
	});
}

function setPrevNextDisabled($wrap, itemIndex: number, maxIndex: number): void
{
	const $prev = $wrap.find(`[data-slider-ctrl-prev]`).removeClass("disabled");
	const $next = $wrap.find(`[data-slider-ctrl-next]`).removeClass("disabled");
	if (itemIndex == 0)
	{
		$prev.addClass("disabled");
	}
	if (itemIndex == maxIndex)
	{
		$next.addClass("disabled");
	}
}

function initNavControls($wrap, $slider, $window, goToGroup: (groupIndex: number) => void, itemsPerGroupSettings): void
{
	initNavControlsForType("dot", $wrap, $slider, $window, goToGroup, itemsPerGroupSettings);
	initNavControlsForType("number", $wrap, $slider, $window, goToGroup, itemsPerGroupSettings);
}

function initNavControlsForType(
	type: "dot"|"number",
	$wrap,
	$slider,
	$window,
	goToGroup: (groupIndex: number) => void,
	itemsPerGroupSettings): void
{
	const $ctrlWrap = $wrap.find(`.${type}-controls`).children();
	if ($ctrlWrap.length === 0)
	{
		return;
	}
	const reset = function reset(): void {
		const numItems: number = $slider.children().length;
		const itemsPerGroup: number = getItemsPerGroup($window, itemsPerGroupSettings);
		const numGroups: number = ceil(numItems / itemsPerGroup);
		if (numGroups < 2)
		{
			$ctrlWrap.css({ display: "none" });
			return;
		}
		$ctrlWrap.empty().css({ display: "" });
		each(range(0, numGroups), (groupIndex: number) => {
			const html: string = (type === "dot") ? `<div class="dot">` : `<div class="number">${groupIndex + 1}</div>`;
			$(html).appendTo($ctrlWrap).click(function (ev) {
				goToGroup(groupIndex);
				return false;
			});
		});
	};
	reset();
	$window.on("resize", throttle(reset, 50));
}

function highlightGroupControl($wrap, groupIndex: number): void
{
	const $controls = $wrap.find(".dot-controls .dot, .number-controls .number");
	$controls.removeClass("selected").filter(function () {
		return $(this).index() === groupIndex;
	}).addClass("selected");
}

function calcMaxIndex($slider, $window, itemsPerGroupSettings): number
{
	const numItems: number = $slider.children().length;
	const itemsPerGroup: number = getItemsPerGroup($window, itemsPerGroupSettings);
	const numGroups: number = ceil(numItems / itemsPerGroup);
	if (numGroups < 2)
	{
		return 0;
	}
	const firstIndexOfLastGroup: number = itemsPerGroup * (numGroups - 1);
	const remainder: number = (numItems % itemsPerGroup); // No. of items in last group or 0 if last group is full
	if (remainder === 0)
	{
		return firstIndexOfLastGroup;
	}
	const numItemsMissingInLastGroup: number = itemsPerGroup - remainder;
	return firstIndexOfLastGroup - numItemsMissingInLastGroup;
}

function setWidths($slider, $window, itemsPerGroupSettings): void
{
	const itemsPerGroup: number = getItemsPerGroup($window, itemsPerGroupSettings);
	const $items = $slider.children();
	const numItems: number = $items.length;
	const sliderWidthPercent: number = (100 / itemsPerGroup) * numItems;
	$slider.css({ width: sliderWidthPercent + "%" });
	const itemWidthPercent: number = 100 / numItems;
	$items.css({ width: itemWidthPercent + "%" });
}

function slideToItem($slider, $display, itemIndex: number): void
{
	const $items = $slider.children();
	const itemWidth: number = $items.outerWidth();
	const x: number = -itemIndex * $items.outerWidth();
	$slider.css({ transform: `translateX(${x}px)` });
}

if (!window["iw"])
{
	window["iw"] = {};
}
if (!window["iw"].sliders)
{
	window["iw"].sliders = {};
}
window["iw"].sliders.initFiniteEqualWidthSlider = initSlider;
